import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { listResidents, addResident, removeResident } from '../../lib/assessment-kiosk-admin-api';
import { bhiEnrolledCandidates, collectTags, filterSortCandidates, primaryTag } from '../../lib/bhi-resident-picker';

export function FacilityResidents({ facilityId, patients = [] }) {
  const [residents, setResidents] = useState([]);
  const [adding, setAdding] = useState(null);   // patientId currently being added
  const [error, setError] = useState(null);
  const [activeTag, setActiveTag] = useState(null);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await listResidents(facilityId);
      setResidents(res.residents || res || []);
    } catch (e) { setError(e.message); }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const rosteredIds = useMemo(() => new Set(residents.map((r) => r.patientId)), [residents]);
  const candidates = useMemo(() => bhiEnrolledCandidates(patients, rosteredIds), [patients, rosteredIds]);
  // Total BHI-enrolled in the practice (ignoring the roster) — distinguishes
  // "no BHI patients at all" from "all already rostered" in the empty state.
  const bhiTotal = useMemo(() => bhiEnrolledCandidates(patients, new Set()).length, [patients]);
  const tagChips = useMemo(() => collectTags(candidates), [candidates]);
  const shown = useMemo(
    () => filterSortCandidates(candidates, { tag: activeTag, query }),
    [candidates, activeTag, query],
  );

  async function onAdd(patientId) {
    setAdding(patientId); setError(null);
    try { await addResident(facilityId, patientId); await load(); }
    catch (e) { setError(e.message); }
    finally { setAdding(null); }
  }

  async function onRemove(patientId) {
    if (!window.confirm('Remove this resident from the facility roster?')) return;
    setError(null);
    try { await removeResident(facilityId, patientId); await load(); }
    catch (e) { setError(e.message); }
  }

  // Build grouped candidate rows: a <strong> header row each time the primary tag
  // changes. Group by the lowercased tag (so 'oak'/'Oak' are one group, matching
  // filterSortCandidates); label with the first member's display case.
  const candidateRows = [];
  let lastGroupKey = null;
  for (const c of shown) {
    const label = primaryTag(c) || '(no tag)';
    const groupKey = label.toLowerCase();
    if (groupKey !== lastGroupKey) {
      lastGroupKey = groupKey;
      candidateRows.push(
        <tr key={`grp-${groupKey}`}><td colSpan={3}><strong>{label}</strong></td></tr>,
      );
    }
    candidateRows.push(
      <tr key={c.patientId}>
        <td>{c.displayName} <span className="tabnum" style={{ opacity: 0.6 }}>{c.patientId}</span></td>
        <td style={{ opacity: 0.7 }}>{(c.tags || []).join(', ')}</td>
        <td>
          <button className="btn sm" disabled={adding === c.patientId} onClick={() => onAdd(c.patientId)}>
            {adding === c.patientId ? 'Adding…' : 'Add'}
          </button>
        </td>
      </tr>,
    );
  }

  return (
    <div className="kiosk-subsection">
      <h4>Residents</h4>
      {error && <div className="kiosk-admin-error">{error}</div>}

      {/* Picker — choose from the practice's BHI-enrolled patients not yet on this roster */}
      <div className="kiosk-resident-picker">
        <input type="text" placeholder="Search name or ID" value={query}
          onChange={(e) => setQuery(e.target.value)} />
        {tagChips.length > 0 && (
          <div className="onboard-tag-row">
            <span className="onboard-tag-label">Tags:</span>
            {tagChips.map(({ tag, count }) => (
              <button key={tag}
                className={`onboard-pill ${activeTag === tag ? 'active' : ''}`}
                onClick={() => setActiveTag(activeTag === tag ? null : tag)}>
                {tag} <span className="tabnum">{count}</span>
              </button>
            ))}
            {activeTag && (
              <button className="btn ghost sm" onClick={() => setActiveTag(null)}>Clear tag</button>
            )}
          </div>
        )}
        <table className="kiosk-device-table">
          <thead><tr><th>Patient</th><th>Tags</th><th></th></tr></thead>
          <tbody>
            {candidates.length === 0 && (
              <tr><td colSpan={3} className="kiosk-empty">
                {bhiTotal === 0
                  ? 'No BHI-enrolled patients in this practice yet.'
                  : 'All enrolled BHI patients are already on this roster.'}
              </td></tr>
            )}
            {candidates.length > 0 && shown.length === 0 && (
              <tr><td colSpan={3} className="kiosk-empty">No matches.</td></tr>
            )}
            {candidateRows}
          </tbody>
        </table>
      </div>

      {/* Current roster */}
      <table className="kiosk-device-table">
        <thead><tr><th>Patient ID</th><th>Name</th><th></th></tr></thead>
        <tbody>
          {residents.length === 0 && (
            <tr><td colSpan={3} className="kiosk-empty">No residents enrolled.</td></tr>
          )}
          {residents.map((r) => (
            <tr key={r.patientId}>
              <td className="tabnum">{r.patientId}</td>
              <td>{r.displayName || '—'}</td>
              <td><button className="btn ghost sm" onClick={() => onRemove(r.patientId)}>Remove</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
